import { useState } from "react";
import Ad from "@components/Advertisement/Ad";
import Container from "@components/Container/Container";
import { useWindowInnerWidth } from "@hooks/useWindowInnerWidth";
import ArticleExploreMore from "@pages/Article/components/ArticleExploreMore/ArticleExploreMore";
import ArticleNewsletter from "@pages/Article/components/ArticleNewsletter/ArticleNewsletter";
import ArticleParagraph from "@pages/Article/components/ArticleParagraphElements/storyElement/ArticleParagraph";
import { ArticleStoryElementProps } from "@pages/Article/components/ArticleParagraphElements/utils/types";
import ArticleReadMore from "@pages/Article/components/ArticleReadMore";
import { getIsBrandedContent } from "@pages/Article/utils/helpers";
import { getFormattedTextForAds } from "@pages/Section/helpers";
import { checkIfFromPaidPressRelease, cn } from "@util/helpers";
import { useFeatureFlagEnabled } from "posthog-js/react";

type ParagraphWrapperProps = ArticleStoryElementProps;

/**
 * Renders a wrapper for paragraphs in an article, handling the display of ads, read more sections, and newsletter signups.
 * It manages the layout and visibility of various components based on the article's content and user status.
 */
const ParapraphWrapper = ({
  bodyString,
  article,
  adsWrapperClass,
  isBoxWrapped,
  index,
  gsChannels,
  displayReadMoreAndNewsletterSignupForm,
  className,
  paragraphIndices,
  articleIndex = 0,
  paragraphWidth,
  isFullContent,
}: ParagraphWrapperProps) => {
  const isUserLoginEnabled = useFeatureFlagEnabled("feature_enable_user_login");

  const sections = article?.sections || [];

  const isBrandedContent = getIsBrandedContent(article?.kicker || "");

  const isPaidPressRelease = checkIfFromPaidPressRelease(sections);
  const isDisplayAds =
    !isUserLoginEnabled || (!isBoxWrapped && !isPaidPressRelease);

  const windowInnerSize = useWindowInnerWidth();
  const [adsHeight, setAdsHeight] = useState(0);

  const relatedArticles = article?.relatedArticles || [];

  const noAdsDisplayed = adsHeight <= 5;

  const adsExtraCss = {
    maxWidth: `${windowInnerSize.width}px`,
    marginLeft: `calc(-1*(${windowInnerSize.width}px - 100%) / 2 )`,
    height: noAdsDisplayed ? 0 : `${adsHeight + 125}px`,
    marginBottom: noAdsDisplayed ? "0" : "2rem",
    marginTop: noAdsDisplayed ? "0" : "2rem",
  };

  const mainSection = sections?.[0];
  const isHideAds = noAdsDisplayed;

  const btTags =
    article?.tags?.map(({ name }) => getFormattedTextForAds(name)).join(",") ||
    "";

  const isExploreWidgetEnabled =
    article?.readMoreSectionOrTagData?.entity &&
    "tag" in article.readMoreSectionOrTagData.entity
      ? article?.readMoreSectionOrTagData?.entity?.tag
      : null;

  const totalParagraphs = paragraphIndices?.length || 0;

  return (
    <>
      <ArticleParagraph
        bodyString={bodyString}
        className={cn(className, paragraphWidth)}
      />

      {isFullContent &&
      [
        paragraphIndices?.[2],
        paragraphIndices?.[5],
        paragraphIndices?.[8],
      ].includes(index) ? (
        <Container rootClassName={cn("px-0 no-print", paragraphWidth)}>
          <>
            {paragraphIndices?.[2] === index && totalParagraphs !== 3 ? (
              <div
                className={cn("my-8 md:my-12", adsWrapperClass, {
                  "my-0 min-h-0 border-y-0 bg-none py-0": isHideAds,
                })}
                style={adsExtraCss}
              >
                <Container rootClassName="sticky top-[120px] no-print">
                  <div className="relative mx-auto w-full md:w-10/12 md:px-8 lg:w-8/12">
                    <Ad
                      adType="imu1"
                      setAdsHeight={setAdsHeight}
                      counter={articleIndex}
                      articleId={article?.id}
                      articleTags={btTags}
                      gsChannels={gsChannels}
                      className="flex justify-center"
                    />
                  </div>
                </Container>
              </div>
            ) : null}

            {paragraphIndices?.[5] === index ? (
              <>
                {displayReadMoreAndNewsletterSignupForm &&
                totalParagraphs > 6 &&
                totalParagraphs <= 9 &&
                !isBrandedContent &&
                !isPaidPressRelease ? (
                  <ArticleReadMore
                    readMoreArticle={relatedArticles}
                    linkParams={
                      new URLSearchParams({
                        ref: "article-see-also",
                      })
                    }
                  />
                ) : null}

                {displayReadMoreAndNewsletterSignupForm &&
                sections &&
                sections?.[0].uniqueName !== "thrive" && // Force to not display newsletter for thrive articles
                article?.tags &&
                totalParagraphs > 9 &&
                !isPaidPressRelease ? (
                  <div className={cn("no-print md:-mx- py-10")}>
                    {isExploreWidgetEnabled ? (
                      <div className={cn("py-10 md:-mx-8")}>
                        <ArticleExploreMore
                          keyword={[isExploreWidgetEnabled]}
                        />
                      </div>
                    ) : (
                      <ArticleNewsletter
                        className="no-print mb-6 border border-gray-175 p-6 md:p-8 lg:-mx-8"
                        sections={sections}
                        tags={article?.tags || []}
                      />
                    )}
                  </div>
                ) : null}

                {isDisplayAds && totalParagraphs !== 6 && mainSection ? (
                  <div
                    className={cn(adsWrapperClass, {
                      "min-h-0 border-y-0 bg-none py-0": isHideAds,
                    })}
                    style={adsExtraCss}
                  >
                    <Container rootClassName="sticky top-[120px] no-print">
                      <div className="relative mx-auto w-full md:w-10/12 md:px-8 lg:w-8/12">
                        <Ad
                          adType="midarticlespecial"
                          setAdsHeight={setAdsHeight}
                          counter={articleIndex}
                          articleId={article?.id}
                          articleTags={btTags}
                          gsChannels={gsChannels}
                          className="flex justify-center"
                        />
                      </div>
                    </Container>
                  </div>
                ) : null}
              </>
            ) : null}

            {paragraphIndices?.[8] === index ? (
              <>
                {displayReadMoreAndNewsletterSignupForm &&
                totalParagraphs > 9 &&
                !isPaidPressRelease ? (
                  <ArticleReadMore
                    readMoreArticle={relatedArticles}
                    linkParams={
                      new URLSearchParams({
                        ref: "article-see-also",
                      })
                    }
                  />
                ) : null}

                {isDisplayAds && totalParagraphs !== 9 && mainSection ? (
                  <div
                    className={cn("my-8 md:my-12", adsWrapperClass, {
                      "min-h-0 border-y-0 bg-none py-0": isHideAds,
                    })}
                    style={adsExtraCss}
                  >
                    <Container rootClassName="sticky top-[120px] no-print">
                      <div className="mx-auto w-full md:w-10/12 md:px-8 lg:w-8/12">
                        <Ad
                          adType="imu2"
                          setAdsHeight={setAdsHeight}
                          counter={articleIndex}
                          articleId={article?.id}
                          articleTags={btTags}
                          gsChannels={gsChannels}
                          className="flex justify-center"
                        />
                      </div>
                    </Container>
                  </div>
                ) : null}
              </>
            ) : null}
          </>
        </Container>
      ) : null}
    </>
  );
};

export default ParapraphWrapper;
